import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import api from '../api/api';
import { Loader2, CheckCircle, RotateCcw, ArrowRight, Info, ChevronDown, X } from 'lucide-react';
import RecordButton from '../components/mockInterview/RecordButton';
import { PITCH_TYPE_LABELS } from '../components/mockInterview/pitchShared';
import { pickRecorderMimeType, extensionForMimeType } from '../utils/audioRecording';

// The closing "ask" example varies by pitch setting — mirrors the per-type
// descriptions on the setup screen (PitchSetupTab).
const TEMPLATE_ASK_EXAMPLE = {
  networking: "I'd love to stay in touch — could we connect on LinkedIn?",
  recruiter: "I'd really like to be considered for this role — what would be the best next step?",
  investor: "I'm looking to connect with people who might be a good fit to talk further — do you have 15 minutes this week?",
};

// Four-part first-timer pitch structure shown in the "first pitch ever"
// dropdown: what each part is for, plus a fully-written example (not a
// fill-in-the-blank) so a total beginner can hear how it actually sounds.
const PITCH_TEMPLATE_STEPS = (pitchType) => [
  {
    label: 'Hook',
    description: "Start with your name and a quick label for who you are — it's the first thing they'll remember.",
    example: "Hi, I'm Alex, a junior Marketing major at State University.",
  },
  {
    label: 'What you do',
    description: "Say what you're interested in or working on right now, in plain terms — skip the jargon.",
    example: "I'm really into social media strategy, and this semester I've been running marketing for our school's entrepreneurship club.",
  },
  {
    label: 'Proof point',
    description: "Back it up with one concrete result or short story — a number makes it stick even more.",
    example: "Since I took over, our Instagram following grew from 200 to over 1,500 in three months.",
  },
  {
    label: 'The ask',
    description: "End with exactly what you want next — don't leave them guessing.",
    example: TEMPLATE_ASK_EXAMPLE[pitchType] || "I'd love to connect and hear more about opportunities on your team.",
  },
];

async function fetchPitchSession(id) {
  // ASSUMED CONTRACT: GET /pitch/sessions/:id -> { session: { DurationSeconds, PitchType, TargetRole, Status } }
  const res = await api.get(`/pitch/sessions/${id}`);
  return res.data?.session || null;
}

async function submitPitchAudio(id, blob, recordedSeconds) {
  // Extension must match the blob's real recorded type (see startRecording) — Safari
  // records MP4, not WebM, and mislabeling it breaks playback later.
  const ext = extensionForMimeType(blob.type);
  const fd = new FormData();
  fd.append('audio', blob, `pitch-${id}.${ext}`);
  fd.append('recordedSeconds', String(recordedSeconds));
  // ASSUMED CONTRACT: POST /pitch/sessions/:id/audio (multipart) -> { status: 'uploaded' }
  const res = await api.post(`/pitch/sessions/${id}/audio`, fd, { headers: { 'Content-Type': undefined } });
  return res.data;
}

async function finishPitch(id) {
  // Let the blob settle on the backend before signalling it to start transcription.
  await new Promise((resolve) => setTimeout(resolve, 400));
  // ASSUMED CONTRACT: POST /pitch/sessions/:id/submit -> { status, queued }
  const res = await api.post(`/pitch/sessions/${id}/submit`);
  try {
    const channel = new BroadcastChannel('pitch-sessions');
    channel.postMessage({ type: 'session-ended', id });
    channel.close();
  } catch (err) {
    console.log('Could not broadcast pitch session-ended', err);
  }
  return res.data;
}

function formatDuration(totalSeconds) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function PitchSession() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const { isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [session, setSession] = useState(null);

  const [recordingState, setRecordingState] = useState('idle'); // idle | recording | recorded | uploading
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [finished, setFinished] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const durationSeconds = session?.DurationSeconds || 60;
  const remaining = Math.max(0, durationSeconds - elapsed);

  useEffect(() => {
    if (!sessionId) {
      setLoadError('No session specified — this page should be opened from the elevator pitch setup screen.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchPitchSession(sessionId)
      .then((data) => {
        if (cancelled) return;
        setSession(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Could not load this pitch session. Try again.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  const clearTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const stopRecording = () => {
    clearTimer();
    mediaRecorderRef.current?.stop();
  };

  const startRecording = async () => {
    setSubmitError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickRecorderMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        // recorder.mimeType is what the browser actually used — Safari ignores webm and
        // records mp4/aac regardless of what we asked for, so read it back rather than
        // assuming our preferred mimeType above is what we got.
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || mimeType || 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecordingState('recorded');
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecordingState('recording');
      setElapsed(0);
      startRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const e = Math.min(Math.floor((Date.now() - startRef.current) / 1000), durationSeconds);
        setElapsed(e);
        if (e >= durationSeconds) stopRecording();
      }, 250);
    } catch (err) {
      console.error('Mic access error:', err);
      setSubmitError('Could not access your microphone. Check your browser permissions and try again.');
    }
  };

  const reRecord = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    setRecordingState('idle');
    setSubmitError('');
  };

  const handleRecordButtonClick = () => {
    if (recordingState === 'idle') startRecording();
    else if (recordingState === 'recording') stopRecording();
  };

  const handleSubmit = async () => {
    if (!audioBlob) return;
    setRecordingState('uploading');
    setSubmitError('');
    try {
      await submitPitchAudio(sessionId, audioBlob, elapsed);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setFinished(true);
      finishPitch(sessionId).catch((err) => console.error('finishPitch error:', err));
    } catch (err) {
      console.error('Submit pitch error:', err);
      setSubmitError('Could not submit your pitch. Try again.');
      setRecordingState('recorded');
    }
  };

  useEffect(() => {
    return () => {
      clearTimer();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bgClass = isDarkMode ? 'bg-slate-900 text-slate-100' : 'bg-surface text-slate-900';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-4 text-blue-500" />
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Loading your pitch session...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center max-w-sm px-6">
          <p className="text-red-500 font-semibold mb-2">Something went wrong</p>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>{loadError}</p>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center max-w-sm px-6">
          <CheckCircle size={48} className="text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Pitch recorded</h2>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
            Your pitch is being analyzed. You can close this tab — results will show up in your Elevator Pitch history.
          </p>
        </div>
      </div>
    );
  }

  // Only a fresh / reset session is recordable. Anything already submitted or
  // scored is read-only here — the report lives in the Elevator Pitch history.
  if (session && session.Status && session.Status !== 'in-progress') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${bgClass}`}>
        <div className="text-center max-w-sm px-6">
          <CheckCircle size={48} className="text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">This pitch is already submitted</h2>
          <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
            You can close this tab — open your Elevator Pitch history to see its status or the full report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col items-center px-6 py-10 ${bgClass}`}>
      <div className="w-full max-w-xl">
        <div className={`rounded-2xl border mb-6 ${
          isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-100 border-slate-200'
        }`}>
          <button
            type="button"
            onClick={() => setTipsOpen((o) => !o)}
            aria-expanded={tipsOpen}
            className={`w-full flex items-center justify-between gap-2 p-4 text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}
          >
            <span className="flex items-center gap-2">
              <Info size={16} />
              In case this is your first pitch ever
            </span>
            {tipsOpen
              ? <X size={16} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />
              : <ChevronDown size={16} className={isDarkMode ? 'text-slate-400' : 'text-slate-500'} />}
          </button>
          <div className={`grid transition-all duration-500 ease-in-out ${tipsOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
            <div className="overflow-hidden">
              <div className={`pl-4 pr-4 pb-4 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <p className="mb-3">
                  New to this? Here are four parts to hit, each with a quick example. Use your own words when you record.
                </p>
                <ol className="list-decimal pl-5 space-y-3">
                  {PITCH_TEMPLATE_STEPS(session?.PitchType).map((step) => (
                    <li key={step.label}>
                      <p className="mt-0.5"><strong className={`mr-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{step.label}:</strong>{step.description}</p>
                      <p className={`mt-1 italic ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Example: "{step.example}"
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className={`rounded-2xl border p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
            {PITCH_TYPE_LABELS[session?.PitchType] || 'Elevator Pitch'}
          </span>
          <h1 className="text-lg font-medium leading-relaxed mt-2">
            Deliver your elevator pitch — you have {durationSeconds} seconds.
          </h1>
          {session?.TargetRole && (
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Target role: <span className="font-semibold">{session.TargetRole}</span>
            </p>
          )}
        </div>
        <div className={`flex items-center justify-center mt-2 mb-8`}>
          <p className={`text-md ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Whenever you feel ready, record your answer by pressing the button below</p>
        </div>

        <div className="flex flex-col items-center gap-4">
          <RecordButton state={recordingState} onClick={handleRecordButtonClick} disabled={false} />

          <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            {recordingState === 'idle' && `Press to start — the recording stops automatically at ${formatDuration(durationSeconds)}`}
            {recordingState === 'recording' && 'Recording... press to stop'}
            {recordingState === 'recorded' && `Recorded (${formatDuration(elapsed)}) — review below, or submit`}
            {recordingState === 'uploading' && 'Submitting your pitch...'}
          </p>

          {recordingState === 'recording' && (
            <p className={`text-2xl font-mono tabular-nums ${remaining <= 10 ? 'text-red-500' : (isDarkMode ? 'text-slate-300' : 'text-slate-600')}`}>
              {formatDuration(remaining)}
            </p>
          )}

          <div className={`w-full flex flex-col items-center gap-3 ${recordingState === 'recorded' ? '' : 'opacity-50'}`}>
            <audio controls src={recordingState === 'idle' ? undefined : audioUrl || undefined} className="w-full max-w-xs" />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={reRecord}
                disabled={!(recordingState === 'recorded' && audioUrl)}
                className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg ${
                  isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                <RotateCcw size={16} />
                Re-record
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!(recordingState === 'recorded' && audioUrl)}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white"
              >
                Submit &amp; Finish
                <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {submitError && <p className="text-sm text-red-500 text-center">{submitError}</p>}
        </div>
      </div>
    </div>
  );
}
